// IMPORTANT: refer and update this before / after making any changes https://www.notion.so/fazzfinancialgroup/React-Error-Boundary-Implementation-19a5d9f672cf80a8a7e4cf4f4cd4ed73

import React from 'react'
import { datadogRum } from '@datadog/browser-rum'
import ErrorPage from '~/components/ErrorPage'
import PageLoader from '~/components/PageLoader'
import { attemptChunkLoadAutoReload, isChunkLoadError } from '~/helpers/chunkLoadRecovery'

type Props = {
  children: React.ReactNode
  fallback?: React.ReactNode
}

type State = {
  hasError: boolean
  error?: Error
  isReloading: boolean
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, isReloading: false }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (isChunkLoadError(error) && attemptChunkLoadAutoReload(error, 'react-error-boundary')) {
      this.setState({ isReloading: true })
      return
    }

    console.error('Error:', error, info)

    datadogRum.addError(error, {
      message: error.message,
      info: info.componentStack,
      source: 'react-error-boundary',
      errorCategory: isChunkLoadError(error) ? 'chunk-load' : 'render',
    })
  }

  render(): React.ReactNode {
    if (this.state.isReloading) {
      return <PageLoader />
    }

    if (this.state.hasError) {
      return this.renderFallback()
    }

    return this.props.children
  }

  private renderFallback(): React.ReactNode {
    const { fallback } = this.props

    if (fallback) return fallback

    if (isChunkLoadError(this.state.error)) {
      return (
        <ErrorPage
          subtitle="We couldn't load part of the app."
          errorMessage="This is usually caused by an unstable connection, a VPN, or a content blocker. Please check your network settings and refresh the page. If it keeps happening, please contact our support team."
        />
      )
    }

    return <ErrorPage subtitle="Something went wrong." />
  }
}
